import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, Roles } from "../../providers/AuthContext";
import { useNotify } from "../../api/Notify";
import { getLogs } from "../../api/Telemetry";
import toast from "react-hot-toast";

import LogTable, { mapHubLogToRow } from "../../components/logs/LogsTable";
import { getStatusLabel } from "../../helpers/StatusMapper";

const ALL_LOG_TYPES = [
    "Status",
    "Authorization",
    "Transaction",
    "Bootnotification",
    "Heartbeat",
    "Charging-Command",
    "Change-Request",
    "Booking-Request",
    "Charge-Attempt",
];

const DEFAULT_SELECTED_LOG_TYPES = [
    "Status",
    "Authorization",
    "Transaction",
    "Bootnotification",
    "Charging-Command",
    "Change-Request",
    "Booking-Request",
    "Charge-Attempt",
];

const SERVICE_PARTNER_LOG_TYPES = [
    "Status",
    "Bootnotification",
    "Heartbeat",
];

const UI_TO_API_LOG_TYPES = {
    Status: ["ChargingPointStatus", "ChargingConnectorStatus", "NotifyEvent"],
    Authorization: ["Authorization"],
    Transaction: ["TransactionStatus"],
    Bootnotification: ["BootNotification"],
    Heartbeat: ["Heartbeat"],
    "Charging-Command": ["ChargingCommand"],
    "Change-Request": ["ChangeRequest"],
    "Booking-Request": ["BookingRequest"],
    "Charge-Attempt": ["ChargeAttempt"],
};

const PointStatusLog = ({
    pointPublicId,
    connectorPublicId,
    dateFrom,
    dateTo,
    useSse = true,
    sessionTime,
}) => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth();
    const isServicePartner = hasRole(Roles.ServicePartner);
    const availableLogTypes = isServicePartner
        ? SERVICE_PARTNER_LOG_TYPES
        : ALL_LOG_TYPES;
    const defaultSelectedLogTypes = isServicePartner
        ? SERVICE_PARTNER_LOG_TYPES
        : DEFAULT_SELECTED_LOG_TYPES;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [search, setSearch] = useState("");

    const [pagination, setPagination] = useState({
        totalCount: 0,
        page: 1,
        pageSize: 100,
        totalPages: 1,
    });

    const [selectedLogTypes, setSelectedLogTypes] = useState(
        defaultSelectedLogTypes
    );

    const selectedLogTypesRef = useRef(selectedLogTypes);
    const refreshTimerRef = useRef(null);

    useEffect(() => {
        selectedLogTypesRef.current = selectedLogTypes;
    }, [selectedLogTypes]);

    const normalizedSearch = search.trim();
    const canSearch =
        normalizedSearch.length === 0 || normalizedSearch.length >= 3;

    const buildBaseRequest = useCallback(
        (extra = {}) => {
            const request = {
                chargingPointPublicIds: [pointPublicId],
                page,
                pageSize,
                sortColumn: "createdDateTimeUtc",
                sortOrder: "desc",
                ...((normalizedSearch.length === 0 ||
                    normalizedSearch.length >= 3) &&
                    normalizedSearch
                    ? { search: normalizedSearch }
                    : {}),
                ...extra,
            };

            if (dateFrom) {
                request.startDateTime = new Date(dateFrom).toISOString();
            }

            if (dateTo) {
                request.endDateTime = new Date(dateTo).toISOString();
            }

            if (connectorPublicId) {
                request.chargingConnectorPublicIds = [connectorPublicId];
            }

            return request;
        },
        [
            pointPublicId,
            page,
            pageSize,
            normalizedSearch,
            dateFrom,
            dateTo,
            connectorPublicId,
        ]
    );

    const fetchLogs = useCallback(
        async ({ extraRequest = {}, selectedTypesOverride = null } = {}) => {
            const token = await getAccessToken();

            const effectiveSelectedTypes =
                selectedTypesOverride ?? selectedLogTypesRef.current;

            const apiLogTypes = Array.from(
                new Set(
                    effectiveSelectedTypes.flatMap(
                        (type) => UI_TO_API_LOG_TYPES[type] ?? []
                    )
                )
            );

            const request = buildBaseRequest({
                ...extraRequest,
                ...(apiLogTypes.length ? { logTypes: apiLogTypes } : {}),
            });

            const response = await getLogs(token, request);
            const rawLogs = response?.logs ?? response?.items ?? [];

            setPagination({
                totalCount: response?.totalCount ?? 0,
                page: response?.page ?? page,
                pageSize: response?.pageSize ?? pageSize,
                totalPages: response?.totalPages ?? 1,
            });

            return rawLogs
                .map(mapHubLogToRow)
                .filter(Boolean)
                .sort((a, b) => (b.dt || 0) - (a.dt || 0));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
        [getAccessToken, buildBaseRequest, page, pageSize]
    );
    const refreshLogs = useCallback(async ({ selectedTypesOverride = null } = {}) => {
        if (!canSearch) return;

        try {
            const mapped = await fetchLogs({ selectedTypesOverride });
            setRows(mapped);
        } catch (err) {
            console.error("Refresh logs failed", err);
        }
    }, [fetchLogs, canSearch]);

    const scheduleRefresh = useCallback(() => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        refreshTimerRef.current = setTimeout(() => {
            refreshLogs();
        }, 500);
    }, [refreshLogs]);

    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setSelectedLogTypes((prev) => {
            const filtered = prev.filter((type) =>
                availableLogTypes.includes(type)
            );

            return filtered.length ? filtered : defaultSelectedLogTypes;
        });
    }, [availableLogTypes, defaultSelectedLogTypes]);

    const handleToggleLogType = useCallback((type) => {
        setPage(1);

        setSelectedLogTypes(prev => {
            const exists = prev.includes(type);
            const next = exists ? prev.filter(x => x !== type) : [...prev, type];

            refreshLogs({ selectedTypesOverride: next });

            return next;
        });
    }, [refreshLogs]);

    const handlePageChange = useCallback((nextPage) => {
        setPage(nextPage);
    }, []);

    const handlePageSizeChange = useCallback((nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
    }, []);

    const handleSearchChange = useCallback((value) => {
        setSearch(value);
        setPage(1);
    }, []);

    useEffect(() => {
        if (!canSearch) return;

        let mounted = true;

        const load = async () => {
            setLoading(true);

            try {

                const mapped = await fetchLogs();

                if (!mounted) return;
                setRows(mapped);
            } catch (e) {
                console.error(e);
                toast.error(t("logs.failedFetch"));
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [fetchLogs, canSearch]);

    const isUiTypeSelected = useCallback(
        (uiType) => {
            const types = selectedLogTypesRef.current;
            if (!types.length) {
                return defaultSelectedLogTypes.includes(uiType);
            }
            return types.includes(uiType);
        },
        [defaultSelectedLogTypes]
    );

    const { connected } = useNotify(
        useSse
            ? [
                `chargingpoints-logs:${pointPublicId}`,
                `status:${pointPublicId}`,
                `charging-commands:p-${pointPublicId}`,
            ]
            : [],
        async (msg) => {
            const meta = msg?.data?.Metadata;
            const eventName = msg?.event ?? "";
            const messageType = (meta?.MessageType ?? "").toLowerCase();

            const sseConnectorPublicId =
                msg?.data?.Data?.ChargingConnectorPublicId ??
                msg?.data?.Data?.chargingConnectorPublicId ??
                null;

            const isOtherConnectorEvent =
                connectorPublicId &&
                sseConnectorPublicId &&
                sseConnectorPublicId !== connectorPublicId;



            try {
                if (eventName.startsWith("status:")) {
                    if (!isUiTypeSelected("Status")) return;
                    if (isOtherConnectorEvent) return;

                    scheduleRefresh();
                    return;
                }

                if (eventName.startsWith("charging-commands")) {
                    if (!isUiTypeSelected("Charging-Command")) return;
                    if (isOtherConnectorEvent) return;

                    scheduleRefresh();
                    return;
                }

                if (messageType.includes("authorization")) {
                    if (!isUiTypeSelected("Authorization")) return;
                    scheduleRefresh();
                    return;
                }

                if (messageType.includes("boot")) {
                    if (!isUiTypeSelected("Bootnotification")) return;
                    scheduleRefresh();
                    return;
                }

                if (messageType.includes("heartbeat")) {
                    if (!isUiTypeSelected("Heartbeat")) return;
                    scheduleRefresh();
                    return;
                }
                if (messageType.includes("transaction")) {
                    if (!isUiTypeSelected("Transaction")) return;
                    if (isOtherConnectorEvent) return;

                    scheduleRefresh();
                    return;
                }

                if (messageType.includes("bookingrequest")) {
                    if (!isUiTypeSelected("Booking-Request")) return;
                    if (isOtherConnectorEvent) return;

                    scheduleRefresh();
                    return;
                }

                if (messageType.startsWith("chargingpoint") || messageType.includes("notifyevent")) {
                    if (!isUiTypeSelected("Status")) return;
                    if (isOtherConnectorEvent) return;

                    scheduleRefresh();
                }
            } catch (err) {
                console.error("SSE refresh failed", err);
            }
        }
    );

    return (
        <>
            {useSse && (
                <div className="w-fit mb-2">
                    <span
                        className={`${connected
                                ? "bg-green-100 text-green-700 border-green-300"
                                : "bg-red-100 text-red-700 border-red-300"
                            } text-[9px] px-1 py-[1px] rounded-sm font-bold leading-none border`}
                    >
                        {getStatusLabel(connected ? "LIVE" : "DISCONNECTED", t)}
                    </span>
                </div>
            )}

            <LogTable
                rows={rows}
                loading={loading}
                sessionTime={sessionTime}
                pagination={pagination}
                allLogTypes={availableLogTypes}
                selectedLogTypes={selectedLogTypes}
                onToggleLogType={handleToggleLogType}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                search={search}
                onSearchChange={handleSearchChange}
                minSearchLength={3}
            />
        </>
    );
};

export default PointStatusLog;
